import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";  

import { ProjectFormProvider, useProjectFormStore } from "@/store/project-form-provider";
import { formatPercent } from "@/utils/format";
import { usePlanGuard } from "@/utils/use-plan-guard";

import ProjectGeneral from "@/pages/ProjectGeneral";
import ProjectProduction from "@/pages/ProjectProduction";
import ProjectSales from "@/pages/ProjectSales";
import ProjectCapex from "@/pages/ProjectCapex";
import ProjectOpEx from "@/pages/ProjectOpex";
import ProjectFinancing from "@/pages/ProjectFinancing";
import ProjectTaxes from "@/pages/ProjectTaxes";
import ProjectAdvanced from "@/pages/ProjectAdvanced";
import ProjectDebt from "@/pages/ProjectDebt";
import ProjectCashFlows from "@/pages/ProjectCashFlows";
import ProjectResults from "@/pages/ProjectResults";
import ProjectFinancialModel from "@/pages/ProjectFinancialModel";
import ProjectExport from "@/pages/ProjectExport";


import ProjectOpEx2 from "@/pages/ProjectOpex2";

// Tabs only visible to paid plans (pro/enterprise). Add/remove keys here.
const PAID_TABS = new Set<string>([
  "opex2",
  "advanced",
  "fimo",
  "export",
]);


function DirtyIndicator() {
  const { isAnyDirty } = useProjectFormStore();
  const { t } = useTranslation();

  if (!isAnyDirty) return null;
  return (
    <span className="inline-flex items-center gap-2 text-xs text-elani-orange bg-elani-orange/10 px-2.5 py-1 rounded-full">
      <span className="h-2 w-2 rounded-full bg-elani-orange animate-pulse" />
      {t("project.status.unsaved_changes")}
    </span>
  );
}


function ProjectHeader() {
  const { project, loading } = useProjectFormStore();
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex justify-between items-center">
      <div className="grid grid-cols-2 gap-6">  
        <div className="font-semibold flex items-center text-lg">
          {t("common.project")}: { loading ? t("common.loading") : (project?.name as string) || t("common.na") }
        </div>
        <div className="inline-flex items-center gap-2">
          <DirtyIndicator />
        </div>
      </div>
      <div className="flex gap-6 text-right">
        <div className="grid grid-cols-2 gap-6">
          <div className="text-center">
            <div className="text-sm font-medium text-muted-foreground mb-1">{t("parameters.project_irr")}</div>
            <div className={`text-lg font-bold`}>
              {formatPercent(project?.irr_project_unlevered_pre_tax as number | null | undefined)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm font-medium text-muted-foreground mb-1">{t("parameters.equity_irr")}</div>
            <div className={`text-lg font-bold`}>
              {formatPercent(project?.irr_equity_levered_pre_tax as number | null | undefined)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Tabs({ activeTab, setActiveTab, isPaidPlan }: { activeTab: string; setActiveTab: (k: string) => void; isPaidPlan: boolean; }) {

  const { isDirty, isOpexDirty } = useProjectFormStore();
  const { t } = useTranslation();

  // Define all tabs with their keys, labels, and dirty status
  const tabs = [
    { key: "general", label: t("project.tabs.general"), dirty: isDirty },
    { key: "production", label: t("project.tabs.production"), dirty: isDirty },
    { key: "sales", label: t("project.tabs.sales"), dirty: isDirty },
    { key: "opex", label: t("project.tabs.opex"), dirty: isDirty },
    // { key: "opex2", label: t("project.tabs.opex"), dirty: isOpexDirty },
    { key: "financing", label: t("project.tabs.financing"), dirty: isDirty },
    { key: "taxes", label: t("project.tabs.taxes"), dirty: isDirty },
    { key: "advanced", label: t("project.tabs.advanced"), dirty: isDirty },
    { key: "results", label: t("project.tabs.results") },
    { key: "debt", label: t("project.tabs.debt") },
    { key: "cash-flows", label: t("project.tabs.cash_flows") },
    { key: "fimo", label: t("project.tabs.fimo") },
    { key: "export", label: t("project.tabs.export") },
  ];


  return (
    <div className="mb-6 flex flex-wrap gap-2">
      {tabs
        .filter((t) => !PAID_TABS.has(t.key) || isPaidPlan)
      .map((tab) => (
        <div key={tab.key} className="flex flex-col items-start">
          <div
            className="text-[10px] leading-none uppercase tracking-wide text-gray-500 mb-1 min-h-[12px]"
            aria-hidden={tab.key !== t("project.tabs.general") && tab.key !== t("project.tabs.results")}
          >
            {tab.key === "general" ? t("project.tabs.inputs") : tab.key === "results" ? t("project.tabs.outputs") : ""}
          </div>
          <button
            onClick={() => setActiveTab(tab.key)}
            className={`relative px-3 py-1.5 rounded text-sm ${
              activeTab === tab.key
                ? "bg-elani-blue text-white border-elani-blue"
                : "bg-gray-200 text-gray-800 border-gray-300 hover:bg-gray-300"
            }`}
          >
            {tab.label}
            {
            /*tab.dirty && (
              <span
                className="absolute -top-1 -right-1 inline-block h-2 w-2 rounded-full bg-elani-orange"
                aria-label={t("project.status.unsaved_changes_aria")}
                title={t("project.status.unsaved_changes_title")}
              />
            )*/
            }
          </button>
        </div>
      ))}
    </div>
  );
}

function Content({ activeTab }: { activeTab: string }) {
  switch (activeTab) {
    case "general":
      return <ProjectGeneral />;
    case "production":
      return <ProjectProduction />;
    case "sales":
      return <ProjectSales />;
    case "capex":
      return <ProjectCapex />;
    case "opex":
      return <ProjectOpEx />;
    case "opex2":
      return <ProjectOpEx2 />;
    case "financing":
      return <ProjectFinancing />;
    case "taxes":
      return <ProjectTaxes />;
    case "advanced":
      return <ProjectAdvanced />;
    case "debt":
      return <ProjectDebt />;
    case "cash-flows":
      return <ProjectCashFlows />;
    case "results":
      return <ProjectResults />;
    case "fimo":
      return <ProjectFinancialModel />;
    case "export":
      return <ProjectExport />;
    default:
      return null;
  }
}

function BeforeUnloadGuard() {
  const { isAnyDirty } = useProjectFormStore();

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isAnyDirty) return;
      e.preventDefault();
      e.returnValue = ""; // required by some browsers
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isAnyDirty]);

  return null;
}

export default function ProjectLayout() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("general");
  const { plan } = usePlanGuard();

  const { t } = useTranslation();

  const isPaidPlan = plan === "pro" || plan === "enterprise";

  useEffect(() => {
    if (PAID_TABS.has(activeTab) && !isPaidPlan) {
      setActiveTab("general");
    }
  }, [activeTab, isPaidPlan]);

  return (
    <ProjectFormProvider>
      <BeforeUnloadGuard />
      <div className="p-6 text-xs text-gray-700">
        <div className="px-6">
          <ProjectHeader />
          <Tabs activeTab={activeTab} setActiveTab={setActiveTab} isPaidPlan={isPaidPlan} />
          <div className="pt-4">
            <Content activeTab={activeTab} />
          </div>
        </div>
      </div>
    </ProjectFormProvider>
  );
}
